import React, { useEffect, useRef, useState } from "react";
import { Download, ExternalLink, Eye, File, FileImage, FileText, Loader2, ShieldAlert } from "lucide-react";
import { apiFetch, apiUrl } from "../../lib/api";
import { formatAttachmentSize } from "../../lib/compressAttachment";

const safeReference = (item) => ({
  id: item.id,
  originalName: item.originalName,
  mimeType: item.mimeType,
  storedSize: Number(item.storedSize ?? item.originalSize) || 0,
  createdAt: item.createdAt,
  storageMode: item.storageMode,
  externalReference: item.externalReference,
  malwareScanStatus: item.malwareScanStatus || "not_configured",
});
const iconFor = (type) => type?.startsWith("image/") ? FileImage : type === "application/pdf" ? FileText : File;

export default function AttachmentUploader({ module, recordId, attachments = [], onChange, onBusyChange }) {
  const onChangeRef = useRef(onChange);
  const [items, setItems] = useState(Array.isArray(attachments) ? attachments : []);
  const [loading, setLoading] = useState(Boolean(module && recordId));
  const [error, setError] = useState("");

  useEffect(() => { onChangeRef.current = onChange; }, [onChange]);
  useEffect(() => { onBusyChange?.(false); }, [onBusyChange]);
  useEffect(() => {
    if (!module || !recordId) { setLoading(false); return undefined; }
    const controller = new AbortController();
    setLoading(true);
    setError("");
    apiFetch(`/attachments?module=${encodeURIComponent(module)}&recordId=${encodeURIComponent(recordId)}`, { signal: controller.signal })
      .then((result) => {
        const next = Array.isArray(result?.attachments) ? result.attachments : [];
        setItems(next);
        onChangeRef.current?.(next.map(safeReference));
      })
      .catch((requestError) => { if (requestError.name !== "AbortError") setError(requestError.message || "Existing attachments could not be loaded."); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [module, recordId]);

  return (
    <section className="md:col-span-2 space-y-3" aria-label="Record attachments">
      <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 flex gap-3" data-testid="attachment-storage-disabled">
        <ShieldAlert className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
        <div><div className="text-sm font-semibold text-amber-900">Document storage is not configured.</div><p className="text-[11px] text-amber-800 mt-1">New binary uploads and attachment deletion are disabled. Authorized historical files remain available and are preserved.</p></div>
      </div>
      {loading && <div className="flex items-center gap-2 text-xs text-slate-500"><Loader2 className="w-4 h-4 animate-spin" /> Loading historical attachments…</div>}
      {error && <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">{error}</div>}
      {items.length > 0 && (
        <div className="rounded-xl border border-slate-200 bg-white divide-y divide-slate-100">
          {items.map((item) => {
            const Icon = iconFor(item.mimeType);
            const isExternal = item.storageMode === "external_reference" && item.externalReference;
            return (
              <div key={item.id} className="flex items-center gap-3 px-3 py-2.5">
                <Icon className="w-4 h-4 text-slate-500 shrink-0" />
                <div className="min-w-0 flex-1"><div className="text-xs font-semibold text-[#082B4D] truncate">{item.originalName}</div><div className="text-[10px] text-slate-500">{formatAttachmentSize(item.storedSize ?? item.originalSize)} · Scan: {(item.malwareScanStatus || "not_configured").replaceAll("_", " ")}</div></div>
                {isExternal ? <a className="p-1.5 text-blue-600" title="Open validated external reference" href={item.externalReference} target="_blank" rel="noopener noreferrer"><ExternalLink className="w-4 h-4" /></a> : <>
                  <button type="button" className="p-1.5 text-[#18B84F]" title="View historical attachment" onClick={() => window.open(apiUrl(`/attachments/${encodeURIComponent(item.id)}`), "_blank", "noopener,noreferrer")}><Eye className="w-4 h-4" /></button>
                  <a className="p-1.5 text-blue-600" title="Download historical attachment" href={apiUrl(`/attachments/${encodeURIComponent(item.id)}?download=1`)}><Download className="w-4 h-4" /></a>
                </>}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
